/* eslint-disable react-hooks/rules-of-hooks */
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { classnames } from 'tailwindcss-classnames';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import ReactPaginate from 'react-paginate';
import { useEffect, useState } from 'react';
import RingLoader from 'react-spinners/RingLoader';
import {
  RecognitionItem,
  IRecognitionItemPageCollection,
  RecognitionItemPageCollection,
  RecognitionProps,
} from 'lib/nmdpconnect';
import Category from './_Category';
import fnNoOp from 'lib/util/no-op';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';

export type { RecognitionProps };

/**
 * EmployeeRecognition component displays a list of employee recognitions with pagination.
 *
 * @param {RecognitionProps} props - The properties for the component.
 * @returns {JSX.Element} - The rendered component.
 */
const EmployeeRecognition = ({ fields }: RecognitionProps): JSX.Element => {
  const submittedText = 'Submitted By';
  if (!fields) {
    return <MissingDataSource />;
  }

  const [currentItems, setCurrentItems] = useState<RecognitionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [componentRef, setComponentRef] = useState<HTMLDivElement | null>(null);
  const [pageCollection] = useState<IRecognitionItemPageCollection>(
    new RecognitionItemPageCollection({
      isLoadingState: [isLoading, setIsLoading],
    })
  );
  pageCollection.componentRef = componentRef;
  const displayPagination = pageCollection.pageCount > 1;

  useEffect(() => {
    const thisComponentRef = componentRef;
    if (!thisComponentRef) {
      return fnNoOp;
    }
    const handleRecognitionPageLoaded = (event: CustomEvent) => {
      setCurrentItems(event.detail.children);
      if (thisComponentRef?.scrollIntoView) {
        thisComponentRef.scrollIntoView({ behavior: 'smooth' });
      }
    };
    thisComponentRef.addEventListener(
      'recognitionPageLoaded',
      handleRecognitionPageLoaded as EventListener
    );

    if (pageCollection.currentPage < 1) {
      pageCollection.loadPage({ page: 1 });
    }
    return () => {
      pageCollection[Symbol.dispose]();
      thisComponentRef.removeEventListener(
        'recognitionPageLoaded',
        handleRecognitionPageLoaded as EventListener
      );
    };
  }, [pageCollection, currentItems, componentRef]);
  const employeeName = ({
    employee,
    employee2,
    employee3,
  }: {
    employee: Field<string>;
    employee2: Field<string>;
    employee3: Field<string>;
  }) => {
    let ret = employee?.value.trim() ?? '';
    const emp2 = employee2?.value?.trim() ?? '';
    const emp3 = employee3?.value?.trim() ?? '';
    if (emp2.length > 0) {
      if (emp3.length > 0) {
        ret += `, ${emp2}, and ${emp3}`;
      } else {
        ret += ` and ${emp2}`;
      }
      return ret;
    }

    return ret;
  };
  const itemClass = classnames('mr-4', 'mb-4', 'leading-6', 'break-inside-avoid-column');

  return (
    <div id="recogntion-section" className={classnames('py-4')} ref={setComponentRef}>
      {(isLoading || pageCollection.currentPage === -1) && (
        <span className={classnames('flex', 'flex-col', 'items-center', 'justify-center', 'pb-2')}>
          <RingLoader color="#00355E" loading aria-label="Please wait - Shout-outs loading" />
        </span>
      )}
      {!isLoading && (
        <>
          <ul role="menu" className={classnames('xl:columns-3', 'md:columns-2')}>
            {currentItems?.map(
              (item) =>
                item && (
                  <li role="menuitem" key={item.id} className={itemClass}>
                    <Category {...item} />
                    <div
                      className={classnames('px-4', 'pt-6', 'pb-4', 'shadow-xl', 'rounded-b-md')}
                    >
                      <div
                        className={classnames(
                          'text-blue-night',
                          'text-xl',
                          'font-semibold',
                          'pb-1'
                        )}
                      >
                        {employeeName({
                          employee: item.fields.employee,
                          employee2: item.fields.employee2,
                          employee3: item.fields.employee3,
                        })}
                      </div>
                      <RichTextA11yWrapper
                        className={classnames('text-body')}
                        field={item.fields.description}
                      />
                      <div className={classnames('text-body', 'italic', 'pt-3')}>
                        {submittedText}: {item.fields.submittedBy?.value}
                      </div>
                    </div>
                  </li>
                )
            )}
          </ul>
          {displayPagination && (
            <div className={classnames('mt-3')}>
              <ReactPaginate
                nextLabel=""
                onPageChange={pageCollection.selectPage}
                pageRangeDisplayed={3}
                marginPagesDisplayed={2}
                pageCount={pageCollection.pageCount}
                previousLabel=""
                pageClassName="pagination-item text-blue-night text-body"
                pageLinkClassName="pagination-link"
                previousClassName="page-item"
                previousLinkClassName="pagination-prev pagination-link"
                nextClassName="page-item"
                nextLinkClassName="pagination-next pagination-link"
                breakLabel="..."
                breakClassName="pagination-item"
                breakLinkClassName="pagination-link"
                containerClassName="pagination-list"
                activeClassName="selected"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeRecognition;
